import { useState } from "react";
import { Eye, EyeOff, Shield, ShieldOff, KeyRound, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { ProviderFormState } from "./types";

const inputClass =
  "w-full h-10 px-3 border border-border bg-background text-sm focus:outline-none focus:border-primary transition-colors";
const labelClass = "text-micro block mb-2";

interface Props {
  form: ProviderFormState;
  update: (field: string, value: any) => void;
}

function generatePassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%";
  let pw = "";
  for (let i = 0; i < 16; i++) pw += chars[Math.floor(Math.random() * chars.length)];
  return pw;
}

export default function TabZugangsdaten({ form, update }: Props) {
  const [showPassword, setShowPassword] = useState(false);

  const syncLoginEmail = () => {
    if (!form.login_email && form.contact_email) {
      update("login_email", form.contact_email);
    }
  };

  return (
    <div className="space-y-8">
      {/* Portal-Zugang */}
      <section className="border border-border bg-card p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-subheadline">Portal-Zugang</h3>
            <p className="text-caption mt-1">Erstellt einen Benutzeraccount für das Provider-Portal</p>
          </div>
          <button
            type="button"
            onClick={() => update("portal_access_enabled", !form.portal_access_enabled)}
            className={cn(
              "flex items-center gap-2 px-4 py-2 text-sm border transition-colors",
              form.portal_access_enabled
                ? "border-accent bg-accent/10 text-accent"
                : "border-destructive bg-destructive/5 text-destructive"
            )}
          >
            {form.portal_access_enabled ? (
              <><Shield className="w-4 h-4" /> Aktiv</>
            ) : (
              <><ShieldOff className="w-4 h-4" /> Gesperrt</>
            )}
          </button>
        </div>
        {!form.portal_access_enabled && (
          <div className="p-4 bg-destructive/5 border border-destructive/20">
            <p className="text-sm text-destructive">
              Der Provider hat keinen Zugang zum Portal. Er kann sich weder anmelden noch Opportunities einsehen.
            </p>
          </div>
        )}
      </section>

      {/* Account erstellen */}
      <section className="border border-border bg-card p-6 space-y-6">
        <div>
          <h3 className="text-subheadline">Benutzer-Account</h3>
          <p className="text-caption mt-1">Login-Daten für den Provider. Der Account wird beim Anlegen automatisch erstellt.</p>
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={form.create_account} onChange={(e) => update("create_account", e.target.checked)} className="w-4 h-4 accent-accent" />
          <span className="text-sm">Account automatisch erstellen</span>
        </label>
        {form.create_account && (
          <>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className={labelClass}>Login E-Mail *</label>
                <div className="flex gap-2">
                  <input type="email" value={form.login_email} onChange={(e) => update("login_email", e.target.value)} placeholder="provider@unternehmen.de" className={cn(inputClass, "flex-1")} />
                  <Button type="button" variant="quiet" size="sm" onClick={syncLoginEmail} className="shrink-0">Aus Kontakt-E-Mail</Button>
                </div>
                <p className="text-micro mt-1">Die E-Mail-Adresse, mit der sich der Provider anmeldet</p>
              </div>
              <div>
                <label className={labelClass}>Initiales Passwort</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input type={showPassword ? "text" : "password"} value={form.initial_password} onChange={(e) => update("initial_password", e.target.value)} placeholder="Wird automatisch generiert falls leer" className={inputClass} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button type="button" variant="quiet" size="sm" onClick={() => update("initial_password", generatePassword())} className="shrink-0">
                    <KeyRound className="w-4 h-4 mr-1" /> Generieren
                  </Button>
                  {form.initial_password && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(form.initial_password); toast.success("Passwort kopiert"); }} className="shrink-0">
                      <Copy className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <p className="text-micro mt-1">Mindestens 6 Zeichen. Provider sollte das Passwort nach dem ersten Login ändern.</p>
              </div>
            </div>
            <div className="p-4 bg-secondary/30 border border-border space-y-2">
              <span className="text-micro block">Hinweise</span>
              <ul className="text-caption space-y-1.5">
                <li className="flex items-start gap-2"><span className="w-1 h-1 bg-accent rounded-full mt-2 shrink-0" />Der Provider erhält eine E-Mail mit seinen Zugangsdaten</li>
                <li className="flex items-start gap-2"><span className="w-1 h-1 bg-accent rounded-full mt-2 shrink-0" />Falls kein Passwort angegeben wird, wird eines automatisch generiert</li>
                <li className="flex items-start gap-2"><span className="w-1 h-1 bg-accent rounded-full mt-2 shrink-0" />Der Zugang kann jederzeit über den Schalter "Portal-Zugang" gesperrt werden</li>
              </ul>
            </div>
          </>
        )}
      </section>

      {/* Zugangs-Übersicht */}
      <section className="border border-border bg-card p-6 space-y-4">
        <h3 className="text-subheadline">Zugangs-Übersicht</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 border border-border bg-secondary/20">
            <span className="text-micro block mb-1">Portal-Zugang</span>
            <p className={cn("text-sm font-medium", form.portal_access_enabled ? "text-accent" : "text-destructive")}>
              {form.portal_access_enabled ? "Aktiviert" : "Gesperrt"}
            </p>
          </div>
          <div className="p-4 border border-border bg-secondary/20">
            <span className="text-micro block mb-1">Account-Erstellung</span>
            <p className="text-sm font-medium">{form.create_account ? "Automatisch beim Anlegen" : "Manuell / Später"}</p>
          </div>
          <div className="p-4 border border-border bg-secondary/20">
            <span className="text-micro block mb-1">Login-E-Mail</span>
            <p className="text-sm font-medium">{form.login_email || form.contact_email || "—"}</p>
          </div>
          <div className="p-4 border border-border bg-secondary/20">
            <span className="text-micro block mb-1">Passwort</span>
            <p className="text-sm font-medium">{form.initial_password ? "Manuell gesetzt" : "Wird generiert"}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
